import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import MuiLinearProgress from '@material-ui/core/LinearProgress';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { capitalize } from '@material-ui/core';
import { darken, lighten } from '@material-ui/core/styles/colorManipulator';

const MUI_COLORS = [
  'primary',
  'secondary',
];

export const RADII = [
  'none',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
];

export const COLORS = [
  ...MUI_COLORS,
  'accentGreen',
  'accentBlue',
  'accentBlue2',
  'darkGray',
  'lightGray',
  'primaryGray',
  'red',
  'orange',
  'seafoam',
  'primaryToAccentBlue',
];

const getColorProp = (color) => {
  if (MUI_COLORS.includes(color)) {
    return color;
  }

  return 'primary'; // Must be this or secondary to satisfy prop types
};

const getRadius = (borderRadius) => {
  return {
    borderRadius,
  };
};

const getBarColor = (backgroundColor) => {
  return {
    backgroundColor,
  };
};

const getRootColor = (theme, backgroundColor) => {
  const getColor = color => (theme.palette.type === 'light' ? lighten(color, 0.62) : darken(color, 0.5));

  return {
    backgroundColor: getColor(backgroundColor),
  };
};

const getBackgroundGradient = (gradient) => {
  return {
    background: gradient,
  };
};


const useCustomStyles = makeStyles(theme => ({
  barColorAccentBlue: getBarColor(theme.palette.facet.accentBlue),
  barColorAccentBlue2: getBarColor(theme.palette.facet.accentBlue2),
  barColorDarkGray: getBarColor(theme.palette.facet.darkGray),
  barColorLightGray: getBarColor(theme.palette.facet.lightGray),
  barColorPrimaryGray: getBarColor(theme.palette.facet.primaryGray),
  barColorRed: getBarColor(theme.palette.facet.red),
  barColorOrange: getBarColor(theme.palette.facet.orange),
  barColorSeafoam: getBarColor(theme.palette.facet.seafoam),
  barColorAccentGreen: getBarColor(theme.palette.facet.accentGreen),
  barColorPrimaryToAccentBlue: getBackgroundGradient(theme.gradients.horizontal.primaryToAccentBlue),
  rootColorAccentBlue: getRootColor(theme, theme.palette.facet.accentBlue),
  rootColorAccentBlue2: getRootColor(theme, theme.palette.facet.accentBlue2),
  rootColorDarkGray: getRootColor(theme, theme.palette.facet.darkGray),
  rootColorLightGray: getRootColor(theme, theme.palette.facet.lightGray),
  rootColorPrimaryGray: getRootColor(theme, theme.palette.facet.primaryGray),
  rootColorRed: getRootColor(theme, theme.palette.facet.red),
  rootColorOrange: getRootColor(theme, theme.palette.facet.orange),
  rootColorSeafoam: getRootColor(theme, theme.palette.facet.seafoam),
  rootColorAccentGreen: getRootColor(theme, theme.palette.facet.accentGreen),
  rootColorPrimaryToAccentBlue: getRootColor(theme, theme.palette.facet.lightGray),
  radiusNone: getRadius(theme.radius.none),
  radiusXs: getRadius(theme.radius.xs),
  radiusSm: getRadius(theme.radius.sm),
  radiusMd: getRadius(theme.radius.md),
  radiusLg: getRadius(theme.radius.lg),
  radiusXl: getRadius(theme.radius.xl),
}));

const styles = theme => ({
  root: {
    height: theme.spacing(1),
  },
  bar: {},
});

function LinearProgress({
  borderRadius,
  className,
  classes,
  color,
  ...rest
}) {
  const {
    root: rootClass,
    bar: barClass,
    ...restClasses
  } = classes;

  const customClasses = useCustomStyles();

  return (
    <MuiLinearProgress
      {...rest}
      classes={{
        ...restClasses,
        root: classNames(
          rootClass,
          customClasses[`rootColor${capitalize(color)}`],
          customClasses[`radius${capitalize(borderRadius)}`],
          className,
        ),
        bar: classNames(
          barClass,
          customClasses[`barColor${capitalize(color)}`],
          customClasses[`radius${capitalize(borderRadius)}`],
        ),
      }}
      color={getColorProp(color)}
    />
  );
}

LinearProgress.defaultProps = {
  borderRadius: 'sm',
  className: '',
  color: 'primary',
};

LinearProgress.propTypes = {
  borderRadius: PropTypes.oneOf(RADII),
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  color: PropTypes.oneOf(COLORS),
};

export default withStyles(styles)(LinearProgress);
